import { Suspense, lazy, useEffect } from 'react'
import { BrowserRouter, Routes, Route, Navigate, Outlet, useLocation, useParams, Location } from 'react-router-dom'
import { useAuthStore } from './store/authStore'
import { can, type Capability } from './utils/permissions'
import { fetchPermissions } from './api/auth'
import { usePermissionsStore } from './store/permissionsStore'
import Toasts from './components/Toasts'
import ImpersonationBanner from './components/ImpersonationBanner'
import { ConfirmHost } from './components/ui/confirm'
import { PageSpinner } from './components/ui/Spinner'
import AppLayout from './components/AppLayout'
import { DASHBOARD_CAPABILITIES } from './components/navigation'
import { fallbackHomePage, resolveHomePage } from './lib/homePage'
import { loginPath } from './utils/loginRedirect'
import Login from './pages/Login'
const Dashboard = lazy(() => import('./pages/Dashboard'))

// Dřívější modulové layouty (components/layouts/*, ModuleLayout) zůstávají v repu, ale nepoužívají se —
// celá aplikace má jedno boční menu v AppLayout (definice položek v components/navigation.ts).
const Revisions = lazy(() => import('./pages/revisions/Revisions'))
const InboxList = lazy(() => import('./pages/inbox/InboxList'))
const InboxDetailPage = lazy(() => import('./pages/inbox/InboxDetail'))
const InboxSettings = lazy(() => import('./pages/inbox/InboxSettings'))
const TasksList = lazy(() => import('./pages/tasks/TasksList'))
const TaskStatusSettings = lazy(() => import('./pages/tasks/TaskStatusSettings'))
const TaskModal = lazy(() => import('./pages/tasks/TaskModal'))

// Attendance pages
const MyHours = lazy(() => import('./pages/attendance/MyHours'))
const Planning = lazy(() => import('./pages/attendance/Planning'))
const WorkerReport = lazy(() => import('./pages/attendance/WorkerReport'))

// Preparations / warehouse pages
const Preparations = lazy(() => import('./pages/Preparations'))
const PreparationDetail = lazy(() => import('./pages/PreparationDetail'))
const PreparationEdit = lazy(() => import('./pages/PreparationEdit'))
const Products = lazy(() => import('./pages/Products'))

// Tool Rental pages
const ToolList = lazy(() => import('./pages/tools/ToolList'))
const ToolDetail = lazy(() => import('./pages/tools/ToolDetail'))
const ToolEdit = lazy(() => import('./pages/tools/ToolEdit'))
const ToolCategories = lazy(() => import('./pages/tools/ToolCategories'))
const InventoryList = lazy(() => import('./pages/tools/InventoryList'))
const MyBorrowings = lazy(() => import('./pages/tools/MyBorrowings'))
const InventoryDetailPage = lazy(() => import('./pages/tools/InventoryDetail'))
const InventoryEvaluationPage = lazy(() => import('./pages/tools/InventoryEvaluation'))

// Vozový park
const VehicleList = lazy(() => import('./pages/vehicles/VehicleList'))
const VehicleDetail = lazy(() => import('./pages/vehicles/VehicleDetail'))

// Admin pages
const Orders = lazy(() => import('./pages/Orders'))
const OrderDetail = lazy(() => import('./pages/OrderDetail'))
const OrderReport = lazy(() => import('./pages/OrderReport'))
const HoursReport = lazy(() => import('./pages/reports/HoursReport'))
const Feeds = lazy(() => import('./pages/admin/Feeds'))
const DocumentEditorPage = lazy(() => import('./pages/documents/DocumentEditorPage'))
const Integrations = lazy(() => import('./pages/admin/Integrations'))
const OrderTypes = lazy(() => import('./pages/admin/OrderTypes'))
const CompanyProfile = lazy(() => import('./pages/admin/CompanyProfile'))
const NumberSeriesPage = lazy(() => import('./pages/admin/NumberSeries'))
const Profile = lazy(() => import('./pages/Profile'))
const StatusSettings = lazy(() => import('./pages/StatusSettings'))
const AttendanceSettings = lazy(() => import('./pages/attendance/AttendanceSettings'))
const AbsencesApproval = lazy(() => import('./pages/attendance/absences/AbsencesApproval'))
const AbsencesOverview = lazy(() => import('./pages/attendance/absences/AbsencesOverview'))
const OvertimeOverview = lazy(() => import('./pages/attendance/OvertimeOverview'))
const OrdersEvaluation = lazy(() => import('./pages/reports/OrdersEvaluation'))
const CompaniesEvaluation = lazy(() => import('./pages/reports/CompaniesEvaluation'))
const EvaluationDetail = lazy(() => import('./pages/reports/EvaluationDetail'))
const InvoicesInbox = lazy(() => import('./pages/reports/InvoicesInbox'))
const Users = lazy(() => import('./pages/Users'))
const Notifications = lazy(() => import('./pages/Notifications'))
const NotificationSettings = lazy(() => import('./pages/NotificationSettings'))
const AuditLog = lazy(() => import('./pages/AuditLog'))
const EmailTemplates = lazy(() => import('./pages/EmailTemplates'))
const RolesPermissions = lazy(() => import('./pages/RolesPermissions'))
const FlowInvoiceEditorPage = lazy(() => import('./pages/orders/FlowInvoiceEditorPage'))

// Service protocols
const ProtocolDetail = lazy(() => import('./pages/protocols/ProtocolDetail'))
const OrderMileages = lazy(() => import('./pages/protocols/OrderMileages'))
const PublicSign = lazy(() => import('./pages/protocols/PublicSign'))

/** Přesměrování na přihlášení s návratem na aktuální stránku (deep linky, např. /inbox/<id>). */
function LoginRedirect() {
  const location = useLocation()
  return <Navigate to={loginPath(location.pathname + location.search)} replace />
}

function PrivateRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated } = useAuthStore()
  return isAuthenticated ? <>{children}</> : <LoginRedirect />
}

/** Staré odkazy na seznam / průvodce protokolů vedou do záložky Dokumenty zakázky. */
function LegacyProtocolsRedirect() {
  const { orderId } = useParams<{ orderId: string }>()
  return <Navigate to={`/attendance/orders/${orderId}?tab=documents`} replace />
}

/**
 * Route guard řízený capability maticí (`utils/permissions`). Centrální náhrada za role-based RoleRoute.
 * Bez oprávnění přesměruje na výchozí úvodní stránku role (Plánování / Moje hodiny) — ty má role
 * vždy dostupné, takže nevznikne smyčka přesměrování.
 */
function CapabilityRoute({ children, capability }: { children: React.ReactNode; capability: Capability | Capability[] }) {
  const { isAuthenticated, user } = useAuthStore()
  // Odběr store, aby se guard přepočítal po načtení /auth/permissions.
  usePermissionsStore((s) => s.capabilities)
  const permissionsLoaded = usePermissionsStore((s) => s.loaded)
  if (!isAuthenticated) return <LoginRedirect />
  const caps = Array.isArray(capability) ? capability : [capability]
  // Stačí kterákoli z uvedených schopností (např. správa nepřítomností NEBO jejich schvalování).
  if (!caps.some((c) => can(user, c))) {
    // Schopnost zapnutá jen v upravené matici tenanta (např. Revize) nemá výchozí fallback — po reloadu
    // počkat na /auth/permissions, jinak by přímý odkaz přesměroval dřív, než se načte.
    if (!permissionsLoaded) return null
    return <Navigate to={fallbackHomePage(user)} replace />
  }
  return <>{children}</>
}

/**
 * „/" = úvodní stránka podle role. Cestu určuje `permissionsStore.homePage` (z /auth/permissions);
 * bez ní (nebo když na ni uživatel nemá přístup) plánovač → Plánování, ostatní → Moje hodiny.
 * Čeká na načtení oprávnění, aby se nepřesměrovalo podle staré výchozí matice.
 */
function HomeRedirect() {
  const { isAuthenticated, user } = useAuthStore()
  const loaded = usePermissionsStore((s) => s.loaded)
  const homePage = usePermissionsStore((s) => s.homePage)
  usePermissionsStore((s) => s.capabilities)
  if (!isAuthenticated) return <Navigate to="/login" replace />
  if (!loaded) return <PageSpinner className="min-h-app" />
  return <Navigate to={resolveHomePage(user, homePage)} replace />
}

/**
 * Vstupní redirect administrace — admin míří na Uživatele, ostatní (skladník/manažer) na Integrace.
 */
function AdminIndexRedirect() {
  const { user } = useAuthStore()
  return <Navigate to={can(user, 'admin.users') ? '/admin/users' : '/admin/integrations'} replace />
}

/**
 * Staré odkazy na /admin/settings: kotvy feedů/importů → Feedy, sazeb → Nastavení docházky,
 * účtu/hesla → Můj profil, ostatní → Integrace.
 */
function LegacySettingsRedirect() {
  const { hash } = useLocation()
  const h = hash.toLowerCase()
  const to = /feed|import/.test(h) ? '/admin/feeds'
    : /rate|sazb/.test(h) ? '/attendance/settings?tab=rates'
      : /account|password|heslo|profil/.test(h) ? '/profile'
        : '/admin/integrations'
  return <Navigate to={to} replace />
}

/**
 * Hlavní router. Podporuje pattern „modal nad pozadím":
 * - Z listu úkolů se naviguje na `/tasks/detail/:id` se `state.backgroundLocation`. Pak se
 *   pozadí renderuje podle backgroundLocation a modal nad ním.
 * - Pokud uživatel přijde na `/tasks/detail/:id` přímo (kopírovaný odkaz), `backgroundLocation`
 *   chybí — modal se zobrazí nad výchozím `/tasks` výpisem.
 *
 * Všechny moduly sdílí jeden layout s bočním menu (AppLayout). Mimo něj zůstávají přihlášení,
 * veřejný podpis a celoobrazovkové servisní protokoly (podpis na tabletu).
 */
function AppRoutes() {
  const location = useLocation()
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)
  const state = location.state as { backgroundLocation?: Location } | null
  const backgroundLocation = state?.backgroundLocation

  // Po startu/reloadu (persistovaná session) načti aktuální matici oprávnění pro runtime can().
  useEffect(() => {
    if (isAuthenticated) {
      fetchPermissions()
    } else {
      // Po odhlášení zahodit oprávnění předchozího uživatele (hlavně jeho rozšířená oprávnění).
      usePermissionsStore.getState().clear()
    }
  }, [isAuthenticated])

  // Pokud jsme na detail URL bez backgroundLocation (= direct URL), renderujeme jako pozadí
  // výchozí list úkolů, aby modal měl nad čím viset.
  const isStandaloneTaskDetail = location.pathname.startsWith('/tasks/detail/') && !backgroundLocation
  const effectiveBackground = backgroundLocation
    ?? (isStandaloneTaskDetail ? ({ ...location, pathname: '/tasks', search: '', hash: '' } as Location) : undefined)

  return (
    <Suspense fallback={<PageSpinner />}>
      <Routes location={effectiveBackground || location}>
        <Route path="/login" element={<Login />} />

        {/* Public signing page — no auth, no chrome */}
        <Route path="/sign/:id/:token" element={<PublicSign />} />

        {/* Úvodní stránka podle role */}
        <Route path="/" element={<HomeRedirect />} />

        {/* Rozcestník modulů byl zrušen — staré odkazy vedou na úvodní stránku */}
        <Route path="/moduly" element={<Navigate to="/" replace />} />

        {/* Celá aplikace v jednom layoutu s bočním menu */}
        <Route
          element={
            <PrivateRoute>
              <AppLayout />
            </PrivateRoute>
          }
        >
          {/* Servisní protokol — tvorba, úprava a podpis s náhledem dokumentu (vzor Serviqo).
              Pro všechny přihlášené; oprávnění hlídá server (ServiceProtocolPolicy). */}
          <Route path="/service-protocols/:id" element={<ProtocolDetail />} />

          {/* Můj profil — každý přihlášený bez ohledu na roli */}
          <Route path="/profile" element={<Profile />} />

          {/* Přehled pro vedení — dlaždice jen podle oprávnění uživatele */}
          <Route path="/dashboard" element={<CapabilityRoute capability={DASHBOARD_CAPABILITIES}><Dashboard /></CapabilityRoute>} />

          {/* Modul: Docházka — všechny role, admin stránky chráněné uvnitř */}
          <Route path="/attendance">
            <Route index element={<MyHours />} />
            <Route path="report" element={<WorkerReport />} />
            <Route path="manage" element={<CapabilityRoute capability="attendance.planning"><Planning /></CapabilityRoute>} />
            {/* Stránka Budgety zrušena — rozpočet je v detailu zakázky, hlídání v seznamu zakázek (filtr Rozpočet). */}
            <Route path="budget" element={<Navigate to="/attendance/orders?budget=over" replace />} />
            <Route path="settings" element={<CapabilityRoute capability={['admin.settings', 'admin.absenceSettings', 'attendance.ordersManage', 'attendance.overtimeManage']}><AttendanceSettings /></CapabilityRoute>} />
            {/* Dřívější samostatná stránka nepřítomností je záložkou Nastavení docházky */}
            <Route path="absences" element={<CapabilityRoute capability={['attendance.absenceApprove', 'attendance.absenceManageAll']}><AbsencesOverview /></CapabilityRoute>} />
            <Route path="absences/approval" element={<CapabilityRoute capability={['attendance.absenceApprove', 'attendance.absenceManageAll']}><AbsencesApproval /></CapabilityRoute>} />
            <Route path="absence-settings" element={<Navigate to="/attendance/absences/approval" replace />} />
            <Route path="overtime" element={<CapabilityRoute capability="attendance.overtimeView"><OvertimeOverview /></CapabilityRoute>} />
            <Route path="orders" element={<CapabilityRoute capability="attendance.ordersManage"><Orders /></CapabilityRoute>} />
            <Route path="orders/:id" element={<CapabilityRoute capability="attendance.ordersManage"><OrderDetail /></CapabilityRoute>} />
            <Route path="orders/:id/report" element={<CapabilityRoute capability="attendance.ordersManage"><OrderReport /></CapabilityRoute>} />
            {/* Faktura z Flow: invoiceId = „new" (výběr podkladů, ?orders=a,b pro víc zakázek) nebo ID faktury */}
            <Route path="orders/:id/invoices/:invoiceId" element={<CapabilityRoute capability="attendance.ordersManage"><FlowInvoiceEditorPage /></CapabilityRoute>} />
            {/* Mzdový a zakázkový report jsou sloučené v Reporty → Hodiny a mzdy */}
            <Route path="payroll" element={<Navigate to="/reports/hours?view=workers" replace />} />
            <Route path="order-report" element={<Navigate to="/reports/hours?view=orders" replace />} />
          </Route>

          {/* Hodiny a mzdy — v Reportech, ale řízené vlastními schopnostmi (ne module.reports / skrytím Reportů) */}
          <Route path="/reports/hours" element={<CapabilityRoute capability={['attendance.payroll', 'attendance.orderReport']}><HoursReport /></CapabilityRoute>} />

          {/* Modul: Reporty pro majitele */}
          <Route path="/reports" element={<CapabilityRoute capability="module.reports"><Outlet /></CapabilityRoute>}>
            <Route index element={<CapabilityRoute capability="reports.view"><OrdersEvaluation /></CapabilityRoute>} />
            <Route path="companies" element={<CapabilityRoute capability="reports.view"><CompaniesEvaluation /></CapabilityRoute>} />
            <Route path="evaluation/:scope/:id" element={<CapabilityRoute capability="reports.view"><EvaluationDetail /></CapabilityRoute>} />
            <Route path="invoices" element={<CapabilityRoute capability="reports.view"><InvoicesInbox /></CapabilityRoute>} />
          </Route>

          {/* Modul: Doklady z e-mailu — sběrný koš vytěžených dokladů */}
          <Route path="/inbox" element={<CapabilityRoute capability="module.inbox"><Outlet /></CapabilityRoute>}>
            <Route index element={<InboxList />} />
            <Route path="settings" element={<InboxSettings />} />
            <Route path=":id" element={<InboxDetailPage />} />
          </Route>

          {/* Modul: Revize — rozšířené oprávnění per uživatel (ADMIN automaticky) */}
          <Route path="/revisions" element={<CapabilityRoute capability="module.revisions"><Outlet /></CapabilityRoute>}>
            <Route index element={<Revisions />} />
          </Route>

          {/* Modul: Přípravy / Sklad — všechny role */}
          <Route path="/preparations">
            <Route index element={<Preparations />} />
            <Route path="statuses" element={<CapabilityRoute capability="admin.statuses"><StatusSettings /></CapabilityRoute>} />
            <Route path=":id" element={<PreparationDetail />} />
            <Route path=":id/edit" element={<PreparationEdit />} />
          </Route>
          <Route path="/products" element={<Products />} />

          {/* Notifikace přihlášeného uživatele (zvoneček → Zobrazit vše) */}
          <Route path="/notifications" element={<Notifications />} />

          {/* Modul: Úkoly — všechny role */}
          <Route path="/tasks">
            <Route index element={<TasksList filter="mine" />} />
            <Route path="created-by-me" element={<TasksList filter="created_by_me" />} />
            <Route path="all" element={<CapabilityRoute capability="tasks.viewAll"><TasksList filter="all" /></CapabilityRoute>} />
            <Route path="statuses" element={<CapabilityRoute capability="tasks.manageStatuses"><TaskStatusSettings /></CapabilityRoute>} />
            {/* detail/:id se renderuje v `AppRoutes` jako modal nad pozadím (modal pattern) */}
          </Route>

          {/* Modul: Půjčovna nářadí — všechny role */}
          <Route path="/tools">
            <Route index element={<ToolList />} />
            <Route path="my-borrowings" element={<MyBorrowings />} />
            <Route path="inventory" element={<CapabilityRoute capability="tools.manage"><InventoryList /></CapabilityRoute>} />
            <Route path="inventory/:id" element={<CapabilityRoute capability="tools.manage"><InventoryDetailPage /></CapabilityRoute>} />
            <Route path="inventory/:id/evaluation" element={<CapabilityRoute capability="tools.manage"><InventoryEvaluationPage /></CapabilityRoute>} />
            <Route path=":id" element={<ToolDetail />} />
            <Route path=":id/edit" element={<CapabilityRoute capability="tools.manage"><ToolEdit /></CapabilityRoute>} />
            <Route path="new" element={<CapabilityRoute capability="tools.manage"><ToolEdit /></CapabilityRoute>} />
            <Route path="categories" element={<CapabilityRoute capability="tools.manage"><ToolCategories /></CapabilityRoute>} />
          </Route>

          {/* Modul: Vozový park */}
          <Route path="/vehicles" element={<CapabilityRoute capability="module.vehicles"><Outlet /></CapabilityRoute>}>
            <Route index element={<VehicleList />} />
            <Route path=":id" element={<VehicleDetail />} />
          </Route>

          {/* Modul: Administrace — ADMIN + WAREHOUSE */}
          <Route path="/admin" element={<CapabilityRoute capability="module.admin"><Outlet /></CapabilityRoute>}>
            <Route index element={<AdminIndexRedirect />} />
            <Route path="users" element={<CapabilityRoute capability="admin.users"><Users /></CapabilityRoute>} />
            {/* Provozovny se už neevidují — staré odkazy vedou na uživatele */}
            <Route path="branches" element={<Navigate to="/admin/users" replace />} />
            <Route path="audit" element={<CapabilityRoute capability="admin.audit"><AuditLog /></CapabilityRoute>} />
            {/* Stavy příprav se přesunuly do sekce Přípravy a vratky */}
            <Route path="statuses" element={<Navigate to="/preparations/statuses" replace />} />
            <Route path="feeds" element={<CapabilityRoute capability="admin.settings"><Feeds /></CapabilityRoute>} />
            <Route path="document-visuals" element={<Navigate to="/document-visuals/editor" replace />} />
            <Route path="integrations" element={<CapabilityRoute capability="admin.settings"><Integrations /></CapabilityRoute>} />
            <Route path="order-types" element={<CapabilityRoute capability="admin.settings"><OrderTypes /></CapabilityRoute>} />
            <Route path="company" element={<CapabilityRoute capability="admin.settings"><CompanyProfile /></CapabilityRoute>} />
            <Route path="number-series" element={<CapabilityRoute capability="admin.settings"><NumberSeriesPage /></CapabilityRoute>} />
            {/* Dřívější Nastavení se rozdělilo na Feedy, Integrace, Nastavení docházky a Můj profil */}
            <Route path="settings" element={<LegacySettingsRedirect />} />
            <Route path="email-templates" element={<CapabilityRoute capability="admin.emailTemplates"><EmailTemplates /></CapabilityRoute>} />
            <Route path="notifications" element={<CapabilityRoute capability="admin.settings"><NotificationSettings /></CapabilityRoute>} />
            <Route path="role-permissions" element={<CapabilityRoute capability="admin.rolesPermissions"><RolesPermissions /></CapabilityRoute>} />
          </Route>
        </Route>

        {/* Editor vizuálů dokumentů — full-screen bez bočního menu (vzor Serviqo) */}
        <Route path="/document-visuals/editor" element={<CapabilityRoute capability="admin.settings"><DocumentEditorPage /></CapabilityRoute>} />

        {/* Dřívější seznam a průvodce protokolů — protokoly jsou v záložce Dokumenty zakázky */}
        <Route path="/orders/:orderId/service-protocols" element={<LegacyProtocolsRedirect />} />
        <Route path="/orders/:orderId/service-protocols/new" element={<LegacyProtocolsRedirect />} />
        <Route
          path="/orders/:orderId/mileages"
          element={
            <PrivateRoute>
              <OrderMileages />
            </PrivateRoute>
          }
        />
      </Routes>

      {/* Modal layer — render jen pokud je aktivní backgroundLocation (= jsme nad pozadím)
          nebo přímý URL pro modal route. */}
      {(backgroundLocation || isStandaloneTaskDetail) && (
        <Routes>
          <Route
            path="/tasks/detail/:id"
            element={
              <PrivateRoute>
                <TaskModal />
              </PrivateRoute>
            }
          />
        </Routes>
      )}
    </Suspense>
  )
}

function App() {
  return (
    <BrowserRouter>
      <Toasts />
      <ConfirmHost />
      <ImpersonationBanner />
      <AppRoutes />
    </BrowserRouter>
  )
}

export default App
